import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal, Toast, useToast } from '../components/ui';
import { dataHora, formatarWhatsapp, hojeISO, num } from '../format';
import { ModalWhatsapp } from './ClienteFicha';
import { useUsuarios } from './Clientes';

type Periodo = 'atrasados' | 'hoje' | 'proximos' | 'concluidos';
interface Retorno {
  id: string; tipo: string; titulo: string; descricao: string | null; venceEm: string; concluidaEm: string | null; origem: string;
  responsavelId: string; responsavel: string;
  clienteId: string | null; clienteNome: string | null; clienteEmpresa: string | null;
  clienteCidade: string | null; clienteUltimaCompra: string | null; clienteValorUltimaCompra: string | null;
  clienteWhatsapp: string | null; clienteComunicacao: string | null; campanha: string | null;
}
interface Resposta { periodo: Periodo; contagem: Record<Periodo, number>; data: Retorno[] }

const ABAS: Array<{ id: Periodo; label: string; cls: string }> = [
  { id: 'atrasados', label: 'Atrasados', cls: 'red' },
  { id: 'hoje', label: 'Hoje', cls: 'amber' },
  { id: 'proximos', label: 'Próximos', cls: 'blue' },
  { id: 'concluidos', label: 'Concluídos hoje', cls: 'green' },
];

const TIPO: Record<string, string> = {
  WHATSAPP: 'WhatsApp', LIGACAO: 'Ligação', REUNIAO: 'Reunião', VISITA: 'Visita', EMAIL: 'E-mail', OUTRO: 'Outro',
};

/** Contagem para o badge do menu (atrasados + hoje). */
export const useContagemRetornos = (habilitado = true) =>
  useQuery({
    queryKey: ['retornos', 'contagem'],
    enabled: habilitado,
    queryFn: () => api<Resposta>('/retornos?periodo=hoje'),
    refetchInterval: 60_000,
    select: (r) => Number(r.contagem.atrasados) + Number(r.contagem.hoje),
  });

export function RetornosPage() {
  const { me, pode } = useAuth();
  const admin = pode('RELATORIOS_TENANT');
  const qc = useQueryClient();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const periodo = (sp.get('periodo') as Periodo) || 'hoje';
  const responsavelId = sp.get('responsavelId') ?? '';
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [reagendar, setReagendar] = useState<Retorno | null>(null);
  const [whatsapp, setWhatsapp] = useState<Retorno | null>(null);
  const usuarios = useUsuarios(admin);

  const qs = new URLSearchParams({ periodo, ...(responsavelId ? { responsavelId } : {}) }).toString();
  const q = useQuery({ queryKey: ['retornos', qs], queryFn: () => api<Resposta>(`/retornos?${qs}`), placeholderData: (p) => p });

  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n, { replace: true });
  };
  const recarregar = () => qc.invalidateQueries({ queryKey: ['retornos'] });

  const concluir = async (r: Retorno, msg = 'Retorno concluído.') => {
    setErro(null);
    setOcupado(r.id);
    try {
      await api(`/retornos/${r.id}/concluir`, { method: 'POST', body: {} });
      await recarregar();
      toast.show(msg);
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setOcupado(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Retornos</h1>
          <span className="muted small">Clientes que você combinou de procurar de novo.</span>
        </div>
        {admin && (
          <div className="actions">
            <select className="select" style={{ width: 220 }} aria-label="Responsável" value={responsavelId} onChange={(e) => set('responsavelId', e.target.value || null)}>
              <option value="">Todos os responsáveis</option>
              {usuarios.data?.filter((u) => u.status === 'ATIVO').map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="row" role="tablist" style={{ gap: 8 }}>
        {ABAS.map((a) => (
          <button key={a.id} role="tab" aria-selected={periodo === a.id} className={`pill${periodo === a.id ? ' on' : ''}`} onClick={() => set('periodo', a.id)}>
            {a.label}
            {q.data && <span className={`badge ${a.cls}`} style={{ marginLeft: 8 }}>{num(Number(q.data.contagem[a.id]))}</span>}
          </button>
        ))}
      </div>

      <Erro erro={erro ?? (q.error ? mensagemErro(q.error) : null)} />

      <div className="card flush">
        {q.isLoading ? <Carregando /> : q.data!.data.length === 0 ? (
          <div className="empty">
            {periodo === 'atrasados' ? 'Nenhum retorno atrasado.' : periodo === 'hoje' ? 'Nenhum retorno para hoje.' : periodo === 'concluidos' ? 'Nenhum retorno concluído hoje.' : 'Nenhum retorno agendado para os próximos dias.'}
          </div>
        ) : (
          q.data!.data.map((r) => (
            <div key={r.id} className="retorno">
              <div className="info">
                <span style={{ fontWeight: 500 }}>
                  {r.clienteId ? <Link to={`/clientes/${r.clienteId}`} style={{ color: 'var(--text)' }}>{r.clienteNome}</Link> : r.titulo}
                  {r.clienteEmpresa && <span className="faint" style={{ fontWeight: 400 }}> · {r.clienteEmpresa}</span>}
                </span>
                <span className="small muted">{r.titulo}{r.descricao ? ` — ${r.descricao}` : ''}</span>
                <span className="small faint num">
                  {periodo === 'concluidos'
                    ? <span style={{ color: 'var(--green-text)' }}>Concluído {dataHora(r.concluidaEm)}</span>
                    : <span style={periodo === 'atrasados' ? { color: 'var(--red-text)' } : undefined}>{dataHora(r.venceEm)}</span>}
                  {' · '}{TIPO[r.tipo] ?? r.tipo}
                  {r.clienteWhatsapp && ` · ${formatarWhatsapp(r.clienteWhatsapp)}`}
                  {admin && ` · ${r.responsavel}`}
                </span>
              </div>
              {periodo !== 'concluidos' && (
                <div className="acoes">
                  {r.clienteId && (r.clienteComunicacao === 'NAO_CONTACTAR'
                    ? <span className="badge red">Não contactar</span>
                    : r.clienteWhatsapp && <button className="btn sm" disabled={ocupado === r.id} onClick={() => setWhatsapp(r)}><Icon name="whatsapp" size={14} />WhatsApp</button>)}
                  <button className="btn sm" disabled={ocupado === r.id} onClick={() => setReagendar(r)}>Reagendar</button>
                  <button className="btn sm primary" disabled={ocupado === r.id} onClick={() => concluir(r)}><Icon name="check" size={14} />Concluir</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {whatsapp && whatsapp.clienteId && (
        <ModalWhatsapp
          cliente={{
            id: whatsapp.clienteId, nome: whatsapp.clienteNome ?? '', empresa: whatsapp.clienteEmpresa, cidade: whatsapp.clienteCidade,
            ultimaCompra: whatsapp.clienteUltimaCompra, valorUltimaCompra: whatsapp.clienteValorUltimaCompra,
          }}
          vendedor={me!.usuario.nome}
          empresa={me!.empresa.nome}
          onClose={() => setWhatsapp(null)}
          onRegistrado={async () => { const r = whatsapp; setWhatsapp(null); await concluir(r, 'WhatsApp registrado e retorno concluído.'); }}
        />
      )}
      {reagendar && (
        <ModalReagendar
          retorno={reagendar}
          onClose={() => setReagendar(null)}
          onFeito={async () => { setReagendar(null); await recarregar(); toast.show('Retorno reagendado.'); }}
        />
      )}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </>
  );
}

function ModalReagendar({ retorno, onClose, onFeito }: { retorno: Retorno; onClose: () => void; onFeito: () => void }) {
  const [data, setData] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const enviar = async (body: { dias?: number; data?: string }) => {
    setErro(null);
    try {
      await api(`/retornos/${retorno.id}/reagendar`, { body });
      onFeito();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };
  return (
    <Modal titulo="Reagendar retorno" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Reagendar retorno</h2>
      <p className="muted" style={{ margin: 0 }}>{retorno.clienteNome ?? retorno.titulo} · o novo horário fica às 09:00.</p>
      <Erro erro={erro} />
      <div className="opts">
        {[[1, 'Amanhã'], [3, 'Em 3 dias'], [7, 'Em 1 semana']].map(([d, l]) => (
          <button key={d} type="button" className="opt" onClick={() => enviar({ dias: d as number })}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <label className="field" style={{ flex: 1 }}><span>Outra data</span>
          <input className="input" type="date" min={hojeISO()} value={data} onChange={(e) => setData(e.target.value)} />
        </label>
        <button className="btn primary" disabled={!data} onClick={() => enviar({ data })}>Salvar</button>
      </div>
    </Modal>
  );
}
